import React, { useMemo, useState } from 'react';
import { format, startOfWeek, endOfWeek, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import Icon from '../Icons/Icon';
import { classNames } from '../../Services/helpers';
import Modal, { useModal } from '../Modal';

const weekOpts = { weekStartsOn: 1 };

const TheCalendar = ({disabled, onSelect, selected, isOpen, setIsOpen}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const days = useMemo(() => {
        return eachDayOfInterval({
            start: startOfWeek(startOfMonth(currentMonth), weekOpts),
            end: endOfWeek(endOfMonth(currentMonth), weekOpts)
        }).map(day => {
            const today = format(new Date(), 'yyyy-MM-dd');
            const date = format(day, 'yyyy-MM-dd');
            return {
                date: date,
                dayOfMonth: format(day, 'd'),
                isToday: date === today,
                isSelected: date === selected,
                isCurrentMonth: format(day, 'yyyy-MM') === format(currentMonth, 'yyyy-MM'),
                isPast: date < today
            };
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMonth, selected]);

    const nextMonth = () => {
        setCurrentMonth(addMonths(currentMonth, 1));
    };

    const prevMonth = () => {
        setCurrentMonth(subMonths(currentMonth, 1));
    };

    return <Modal isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        <InnerCalendar {...{days, currentMonth, nextMonth, prevMonth, onSelect, selected}} />
    </Modal>
};

const InnerCalendar = ({days, currentMonth, nextMonth, prevMonth, onSelect}) => {
    const {closeModal} = useModal();

    const handleSelect = (day) => {
        onSelect(day);
        closeModal();
    }

    return <>
        <div className="flex flex-row justify-around items-center text-center text-gray-900 md:px-5">
            <button
                type="button"
                className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                onClick={prevMonth}
            >
                <span className="sr-only">Previous month</span>
                <Icon icon="LeftChevron" className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="flex-auto text-sm font-semibold grow-0">{format(currentMonth, 'MMMM')}</div>
            <button
                type="button"
                className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                onClick={nextMonth}
            >
                <span className="sr-only">Next month</span>
                <Icon icon="RightChevron" className="h-5 w-5" aria-hidden="true" />
            </button>
        </div>
        <div className="mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
            <div>M</div>
            <div>T</div>
            <div>W</div>
            <div>T</div>
            <div>F</div>
            <div>S</div>
            <div>S</div>
        </div>
        <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
            {days.map((day, dayIdx) => (
            <button
                key={day.date}
                type="button"
                onClick={() => handleSelect(day)}
                className={classNames(
                    'py-1.5 hover:bg-gray-100 focus:z-10',
                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                    (day.isSelected || day.isToday) && 'font-semibold',
                    day.isSelected && 'text-white',
                    !day.isPast && !day.isSelected && day.isCurrentMonth && !day.isToday && 'text-gray-900',
                    ((!day.isSelected && !day.isCurrentMonth && !day.isToday) || day.isPast) && 'text-gray-400',
                    day.isToday && !day.isSelected && 'text-brand',
                    dayIdx === 0 && 'rounded-tl-lg',
                    dayIdx === 6 && 'rounded-tr-lg',
                    dayIdx === days.length - 7 && 'rounded-bl-lg',
                    dayIdx === days.length - 1 && 'rounded-br-lg'
                )}
            >
                <time
                dateTime={day.date}
                className={classNames(
                    'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
                    day.isSelected && day.isToday && 'bg-brand',
                    day.isSelected && !day.isToday && 'bg-brand'
                )}
                >
                {day.dayOfMonth}
                </time>
            </button>
            ))}
        </div>
    </>
}

export default TheCalendar;
